import { LoaderCircle, Pause, Play, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button, type ButtonProps } from '../design-system/button.js';
import { cn } from '../design-system/cn.js';
import {
  usePlaybackTransport,
  type PlaybackItem,
  type PlaybackStatus,
  type PlaybackTransportController,
} from './player-context.js';

export type PlaybackControlKind =
  'play' | 'loading' | 'pause' | 'resume' | 'replay' | 'refresh' | 'retry';

export type PlaybackControlActivation = 'start' | 'toggle' | 'retry' | 'none';

export type PlaybackControlView = {
  activation: PlaybackControlActivation;
  busy: boolean;
  current: boolean;
  kind: PlaybackControlKind;
  label: string;
  pressed: boolean;
  text: string;
};

export type PlaybackControl = PlaybackControlView & {
  activate: () => void;
};

type PlaybackOperations = {
  retry: () => Promise<void> | void;
  start: () => Promise<void> | void;
  toggle: () => Promise<void> | void;
};

export function isCurrentPlaybackItem(
  current: PlaybackItem | null,
  candidate: PlaybackItem,
): boolean {
  return current?.id === candidate.id;
}

export function derivePlaybackControl(
  item: PlaybackItem,
  current: PlaybackItem | null,
  status: PlaybackStatus,
): PlaybackControlView {
  if (!isCurrentPlaybackItem(current, item)) {
    return {
      activation: 'start',
      busy: false,
      current: false,
      kind: 'play',
      label: `Play ${item.title}`,
      pressed: false,
      text: 'Play',
    };
  }

  switch (status) {
    case 'loading':
      return {
        activation: 'none',
        busy: true,
        current: true,
        kind: 'loading',
        label: `Loading ${item.title}`,
        pressed: false,
        text: 'Loading…',
      };
    case 'waiting':
      return {
        activation: 'none',
        busy: true,
        current: true,
        kind: 'loading',
        label: `Buffering ${item.title}`,
        pressed: false,
        text: 'Buffering…',
      };
    case 'playing':
      return {
        activation: 'toggle',
        busy: false,
        current: true,
        kind: 'pause',
        label: `Pause ${item.title}`,
        pressed: true,
        text: 'Pause',
      };
    case 'paused':
      return {
        activation: 'toggle',
        busy: false,
        current: true,
        kind: 'resume',
        label: `Resume ${item.title}`,
        pressed: false,
        text: 'Resume',
      };
    case 'ended':
      return {
        activation: 'toggle',
        busy: false,
        current: true,
        kind: 'replay',
        label: `Replay ${item.title}`,
        pressed: false,
        text: 'Replay',
      };
    case 'source-expired':
      return {
        activation: 'retry',
        busy: false,
        current: true,
        kind: 'refresh',
        label: `Refresh playback source for ${item.title}`,
        pressed: false,
        text: 'Refresh',
      };
    case 'failed':
      return {
        activation: 'retry',
        busy: false,
        current: true,
        kind: 'retry',
        label: `Retry ${item.title}`,
        pressed: false,
        text: 'Retry',
      };
    case 'idle':
      return {
        activation: 'toggle',
        busy: false,
        current: true,
        kind: 'resume',
        label: `Resume ${item.title}`,
        pressed: false,
        text: 'Resume',
      };
  }
}

export function activatePlaybackControl(
  view: PlaybackControlView,
  operations: PlaybackOperations,
): void {
  if (view.activation === 'start') void operations.start();
  if (view.activation === 'toggle') void operations.toggle();
  if (view.activation === 'retry') void operations.retry();
}

export function createPlaybackControl(
  player: PlaybackTransportController,
  item: PlaybackItem,
  start: () => Promise<void> | void = () => player.play(item),
): PlaybackControl {
  const view = derivePlaybackControl(item, player.item, player.status);
  return {
    ...view,
    activate: () =>
      activatePlaybackControl(view, {
        retry: player.retry,
        start,
        toggle: player.toggle,
      }),
  };
}

export function usePlaybackControl(
  item: PlaybackItem | null,
  start?: () => Promise<void> | void,
): PlaybackControl | null {
  const player = usePlaybackTransport();
  if (!item) return null;
  return createPlaybackControl(player, item, start);
}

export function PlaybackControlIcon({
  className,
  kind,
}: {
  className?: string;
  kind: PlaybackControlKind | 'unavailable';
}) {
  if (kind === 'loading') {
    return (
      <LoaderCircle
        aria-hidden="true"
        className={cn('animate-spin motion-reduce:animate-none', className)}
      />
    );
  }
  if (kind === 'pause') {
    return <Pause aria-hidden="true" className={className} fill="currentColor" />;
  }
  if (kind === 'refresh' || kind === 'retry') {
    return <RotateCw aria-hidden="true" className={className} />;
  }
  return <Play aria-hidden="true" className={className} fill="currentColor" />;
}

export interface PlaybackButtonProps extends Omit<
  ButtonProps,
  'aria-busy' | 'aria-label' | 'aria-pressed' | 'children' | 'disabled' | 'onClick'
> {
  control: PlaybackControl | null;
  iconClassName?: string;
  inactiveContent?: ReactNode;
  showLabel?: boolean;
  unavailableLabel: string;
}

export function PlaybackButton({
  className,
  control,
  iconClassName,
  inactiveContent,
  showLabel = false,
  unavailableLabel,
  ...props
}: PlaybackButtonProps) {
  const label = control?.label ?? unavailableLabel;
  return (
    <Button
      {...props}
      aria-busy={control?.busy || undefined}
      aria-label={label}
      aria-pressed={control?.pressed ?? false}
      className={className}
      data-playback-state={control?.kind ?? 'unavailable'}
      disabled={!control || control.busy}
      onClick={control?.activate}
      title={showLabel ? undefined : label}
    >
      {control?.current || !inactiveContent ? (
        <PlaybackControlIcon
          className={cn(control?.kind === 'play' && 'translate-x-px', iconClassName)}
          kind={control?.kind ?? 'unavailable'}
        />
      ) : (
        inactiveContent
      )}
      {showLabel ? (control?.text ?? 'Unavailable') : null}
    </Button>
  );
}
